import * as React from 'react';
import * as Yup from 'yup';
import { useDispatch } from 'react-redux';
import { Form } from 'formik';
import { useTranslation } from 'react-i18next';
import {
  Block,
  Lottie,
  FormRadioButtons,
  FormikWrapper, FormError,
} from '@hvcgroep/design-system';
import lottieHeat from '@hvcgroep/design-system/assets/lotties/warmte-streepjes.json';

import { APP_ID } from '../../../../shared/config/constants';
import { REGISTRATION_TYPE_MOVING, REGISTRATION_TYPE_RENOVATION } from '../../config/constants';
import useAddressFields, {
  AddressSelectionFields,
} from '../../../../shared/containers/AddressSelection/Fields';
import Button from '../../../../shared/components/Button';
import { RegistrationType } from '../../types';
import AddressSelectionInfo from '../../../../shared/containers/AddressSelection/Info';
import { setSelectedGeoLocation } from '../../../../shared/store/actions/GeoLocation';
import { geolocationApi } from '../../../../shared/store/slices/GeolocationApi';

import './style.scss';
import { RegistrationDeskEventHandler } from '../../state-machine';
import { gjtom3 } from '../../helpers/gasCalculation';
import { heatApi } from '../../store/slices/HeatApi';
import trackValidationError from '../../../../shared/helpers/trackValidationError';

type FormState = 'INPUT' | 'ADDRESS_VALID' | 'ADDRESS_INVALID_NO_HEAT';
interface Address {
  nummeraanduiding_id: string;
  straatnaam: string;
  huisnummer: string;
  huisnummertoevoeging: string;
  huisletter: string;
  postcode: string;
  woonplaatsnaam: string;
}

interface Props {
  onAddressSelected: RegistrationDeskEventHandler<'proceed.saveRegistrationType'>
}

const AddressSelection: React.FC<Props> = (props) => {
  const { t } = useTranslation();
  const { onAddressSelected } = props;
  const dispatch = useDispatch();

  const [formState, setFormState] = React.useState<FormState>('INPUT');

  const [submittedAddress, setSubmittedAddress] = React.useState<Address | null>(null);

  const [trigger] = heatApi.endpoints.getOffer.useLazyQuerySubscription();
  const [searchEamData] = heatApi.endpoints.getEamData.useLazyQuery();

  const addressQueryMapper = {
    uid: 'nummeraanduiding_id',
    street: 'straatnaam',
    house_number: 'huisnummer',
    house_number_extension: 'huisnummertoevoeging',
    house_letter: 'huisletter',
    postal_code: 'postcode',
    city: 'woonplaatsnaam',
  } as const;

  const {
    submit: submitAddressFields,
    initialValues,
    validationScheme,
    props: addressFieldProps,
  } = useAddressFields<Address[]>({
    queryMapper: addressQueryMapper,
  });

  return (
    <Block
      padding="large"
    >
      <Lottie
        id="heat-stripes"
        animationData={lottieHeat}
        className="lottie-position--20"
      />
      <h2>{t('registration_desk.address_selection.title')}</h2>
      <FormikWrapper
        initialValues={{
          ...initialValues,
          registration_type: '' as RegistrationType,
        }}
        validateOnBlur={false}
        validateOnChange={false}
        validationSchema={Yup.object()
          .shape({
            ...validationScheme,
            registration_type: Yup.string()
              .required(t('form.error.required')),
          })}
        onSubmit={async (values, helpers) => {
          helpers.setSubmitting(true);

          const existingAddress = await submitAddressFields(values, helpers);

          if (!existingAddress) {
            setFormState('INPUT');
            setSubmittedAddress(null);
            helpers.setSubmitting(false);

            return;
          }

          setSubmittedAddress(existingAddress);

          const { data } = await searchEamData({
            postal_code: existingAddress.postcode,
            housenumber: existingAddress.huisnummer,
          });

          const eamData = data?.find((address) => {
            const normalizedPdokExtension = (`${existingAddress.huisletter || ''}${existingAddress.huisnummertoevoeging || ''}`).toUpperCase() || '';

            return normalizedPdokExtension === address.HousenumberExt;
          });

          if (!eamData) {
            setFormState('ADDRESS_INVALID_NO_HEAT');
            helpers.setSubmitting(false);
            return;
          }

          setFormState('ADDRESS_VALID');

          // Fetch the offer and save it in the redux state.
          await trigger({
            ean: eamData.Ean,
            sjvGJ: eamData.SjvTot,
          });

          // Eventually we send all the address related info to a callback function.
          onAddressSelected({
            eamData,
            registrationType: values.registration_type,
            usageM3: String(gjtom3(Number(eamData.SjvTot))),
          });

          helpers.setSubmitting(false);
        }}
        onValidationError={(errors) => {
          trackValidationError(String(APP_ID), 'AddressSelection', errors);
        }}
      >
        {({
          isSubmitting, resetForm,
        }) => {
          const handleAddressReset = () => {
            dispatch(setSelectedGeoLocation({ data: null }));
            dispatch(geolocationApi.util.resetApiState());
            setFormState('INPUT');
            setSubmittedAddress(null);
            resetForm();
          };

          return (
            <Form className="AddressSelectionForm">
              {formState === 'INPUT' && (
                <>
                  <AddressSelectionFields
                    {...addressFieldProps}
                    isLiveValidationDisabled
                  />

                  <FormRadioButtons
                    name="registration_type"
                    label={t('registration_desk.address_selection.form.legend')}
                    isLiveValidationDisabled
                    options={[{
                      key: REGISTRATION_TYPE_MOVING,
                      value: t('registration_desk.address_selection.form.registration_type.moving'),
                    },
                    {
                      key: REGISTRATION_TYPE_RENOVATION,
                      value: t('registration_desk.address_selection.form.registration_type.renovation'),
                    }]}
                  />
                  <div className="form-item AddressSelectionForm__submit mt-5">
                    <Button
                      type="primary"
                      htmlType="submit"
                      isLoading={isSubmitting}
                    >
                      {t('registration_desk.address_selection.form.button.submit')}
                    </Button>
                  </div>
                </>
              )}
              {formState === 'ADDRESS_INVALID_NO_HEAT' && (
                <>
                  <div className="AddressSelectionForm__no_heat_view">
                    <AddressSelectionInfo
                      address={submittedAddress}
                      variant="full"
                      addressMapper={addressFieldProps.queryMapper}
                      onAddressReset={handleAddressReset}
                    />
                  </div>

                  <div className="mt-5">
                    <FormError>
                      <h3>{t('registration_desk.address_selection.form.error.heat_cannot_be_supplied')}</h3>
                    </FormError>
                  </div>
                  <div className="AddressSelectionForm__more_buttons">
                    <Button
                      type="primary"
                      href="https://www.hvcgroep.nl/warmte/onze-warmtenetten"
                    >
                      {t('registration_desk.address_selection.form.error.view_our_heating_networks')}
                    </Button>

                    <Button
                      type="secondary"
                      href="https://www.hvcgroep.nl/ons-verhaal/klantenservice"
                    >
                      {t('registration_desk.address_selection.form.error.to_customer_service')}
                    </Button>
                  </div>
                </>
              )}
            </Form>
          );
        }}
      </FormikWrapper>
    </Block>
  );
};

export default AddressSelection;
